import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import type { SequenceContainerStepType, SequenceStepType } from '~/components/Sequence/sequenceSteps';
import {
  SEQUENCE_CONTAINER_META,
  SEQUENCE_CONTAINER_STEP_TYPES,
  SEQUENCE_STEP_GROUPS,
  STEP_META,
} from '~/components/Sequence/sequenceSteps';

/**
 * Inline step-type picker shown on the canvas at an insertion point (a small popover menu, not a
 * centered modal). Pick a type to insert a step there; the X cancels. `disabledReason` greys out
 * types that aren't valid at this position (e.g. "Await accept" before any Connect).
 *
 * When `onPickContainer` is provided (the main spine only — arms nest just one level deep), a
 * "Parallel" section offers the container steps (a parallel group / a detached spawn).
 */
export function SequenceStepPicker({
  onPick,
  onPickContainer,
  onCancel,
  disabledReason,
}: {
  onPick: (type: SequenceStepType) => void;
  onPickContainer?: (type: SequenceContainerStepType) => void;
  onCancel: () => void;
  disabledReason?: (type: SequenceStepType) => string | null;
}) {
  return (
    <div
      data-sequence-picker
      className="w-56 overflow-hidden rounded-xl border border-lynch-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between border-b border-lynch-200 px-3 py-2 dark:border-slate-700">
        <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Add a step</span>
        <Button size="2xs" text icon={<General.XMark />} tooltip="Cancel" onClick={onCancel} />
      </div>
      {SEQUENCE_STEP_GROUPS.map((group, gi) => (
        <div
          key={group.label ?? 'actions'}
          className={cn(
            'flex flex-col p-1',
            gi > 0 && 'border-t border-lynch-200 dark:border-slate-700',
          )}>
          {group.label != null && (
            <span className="px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {group.label}
            </span>
          )}
          {group.types.map((type) => {
            const reason = disabledReason?.(type) ?? null;
            return (
              <Button
                key={type}
                text
                size="sm"
                icon={STEP_META[type].icon}
                onClick={() => onPick(type)}
                disabled={reason != null}
                tooltip={reason ?? undefined}
                className="w-full justify-start gap-2.5">
                {STEP_META[type].label}
              </Button>
            );
          })}
        </div>
      ))}
      {onPickContainer != null && (
        <div className="flex flex-col border-t border-lynch-200 p-1 dark:border-slate-700">
          <span className="px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
            Parallel
          </span>
          {SEQUENCE_CONTAINER_STEP_TYPES.map((type) => (
            <Button
              key={type}
              text
              size="sm"
              icon={SEQUENCE_CONTAINER_META[type].icon}
              onClick={() => onPickContainer(type)}
              tooltip={SEQUENCE_CONTAINER_META[type].hint}
              className="w-full justify-start gap-2.5">
              {SEQUENCE_CONTAINER_META[type].label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
